import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiArrowRight, FiActivity, FiClock, FiStar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const TABS = ['Cost Savings', 'Time Saved', 'Customer Experience'];

const ROICalculator = () => {
  const [inputs, setInputs] = useState({
    monthlyInboundCalls: 500,
    missedCallRate: 30,
    avgHandleTime: 8,
    numStaff: 3,
    costPerStaff: 4000,
  });
  const [selectedPlan, setSelectedPlan] = useState(1000);
  const [activeTab, setActiveTab] = useState(0);
  const [results, setResults] = useState({});

  const handleChange = (field, value) => setInputs(prev => ({ ...prev, [field]: value }));

  useEffect(() => {
    const aiHandleRate = 0.65;
    const hourlyRate = (inputs.costPerStaff / 160);
    const missedCalls = Math.round(inputs.monthlyInboundCalls * (inputs.missedCallRate / 100));
    const aiHandledCalls = Math.round(inputs.monthlyInboundCalls * aiHandleRate);
    const totalStaffHours = Math.round((inputs.monthlyInboundCalls * inputs.avgHandleTime) / 60);
    const aiHoursReplaced = Math.round((aiHandledCalls * inputs.avgHandleTime) / 60);
    const workDaysSaved = Math.round(aiHoursReplaced / 8);
    const annualWorkDaysSaved = workDaysSaved * 12;
    const staffCostEquivalent = Math.round(aiHoursReplaced * hourlyRate);
    const netSavings = staffCostEquivalent - selectedPlan;
    const annualSavings = netSavings * 12;
    const roi = selectedPlan > 0 ? Math.round((netSavings / selectedPlan) * 100) : 0;

    setResults({
      missedCalls, aiHandledCalls, totalStaffHours, aiHoursReplaced,
      workDaysSaved, annualWorkDaysSaved, staffCostEquivalent,
      netSavings, annualSavings, roi,
    });
  }, [inputs, selectedPlan]);

  const sliders = [
    { field: 'monthlyInboundCalls', label: 'Monthly Inbound Calls', min: 100, max: 5000, step: 50, display: inputs.monthlyInboundCalls.toLocaleString() },
    { field: 'missedCallRate', label: 'Current Missed Call Rate', min: 5, max: 60, step: 5, display: `${inputs.missedCallRate}%` },
    { field: 'avgHandleTime', label: 'Avg Handle Time (minutes)', min: 2, max: 30, step: 1, display: `${inputs.avgHandleTime} min` },
    { field: 'numStaff', label: 'Support Staff Handling Calls', min: 1, max: 20, step: 1, display: `${inputs.numStaff} people` },
    { field: 'costPerStaff', label: 'Fully-Loaded Cost Per Staff/mo', min: 2000, max: 10000, step: 500, display: `$${inputs.costPerStaff.toLocaleString()}` },
  ];

  return (
    <section id="roi-calculator" className="py-12 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Calculate Your Full ROI</h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-base md:text-lg">
            See the real impact across three dimensions: cost savings, time reclaimed, and customer experience.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Inputs */}
          <div className="lg:col-span-4 space-y-5 bg-slate-50 p-6 rounded-2xl border border-slate-200">
            <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wider">Your Numbers</h3>
            {sliders.map(({ field, label, min, max, step, display }) => (
              <div key={field}>
                <div className="flex justify-between mb-1.5">
                  <label className="text-sm font-medium text-slate-700">{label}</label>
                  <span className="text-sm font-bold text-slate-900">{display}</span>
                </div>
                <input type="range" min={min} max={max} step={step} value={inputs[field]}
                  onChange={e => handleChange(field, parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-green-700" />
              </div>
            ))}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Select Your Plan</label>
              <div className="grid grid-cols-3 gap-2">
                {[500, 1000, 2000].map(p => (
                  <button key={p} onClick={() => setSelectedPlan(p)}
                    className={`py-2 rounded-lg text-sm font-semibold border transition-all ${selectedPlan === p ? 'bg-green-700 text-white border-green-700' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>
                    ${p}/mo
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-8 space-y-5">
            {/* Tabs */}
            <div className="flex rounded-xl bg-slate-100 p-1 gap-1">
              {TABS.map((tab, i) => (
                <button key={i} onClick={() => setActiveTab(i)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${activeTab === i ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab 1: Cost Savings */}
            {activeTab === 0 && (
              <div className="bg-slate-900 rounded-2xl p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <SafeIcon icon={FiActivity} className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold">Cost Savings</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700">
                    <div className="text-xs text-slate-400 mb-1">AI Handles Per Month</div>
                    <div className="text-2xl font-bold">{results.aiHandledCalls?.toLocaleString()} calls</div>
                    <div className="text-xs text-slate-500 mt-1">~65% of your inbound volume</div>
                  </div>
                  <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700">
                    <div className="text-xs text-slate-400 mb-1">Staff Cost Equivalent</div>
                    <div className="text-2xl font-bold">${results.staffCostEquivalent?.toLocaleString()}</div>
                    <div className="text-xs text-slate-500 mt-1">labor cost AI replaces</div>
                  </div>
                  <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700">
                    <div className="text-xs text-slate-400 mb-1">Your Valtivo Investment</div>
                    <div className="text-2xl font-bold">${selectedPlan.toLocaleString()}/mo</div>
                  </div>
                  <div className="bg-green-900/40 p-4 rounded-xl border border-green-500/30">
                    <div className="text-xs text-green-300 mb-1">ROI</div>
                    <div className="text-2xl font-bold text-green-400">{results.roi}%</div>
                  </div>
                </div>
                <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div>
                    <div className="text-xs text-slate-400 mb-1">Net Monthly Savings</div>
                    <div className="text-4xl font-bold text-white">${results.netSavings?.toLocaleString()}</div>
                  </div>
                  <div className="text-center sm:text-right">
                    <div className="text-xs text-slate-400 mb-1">Annual Savings</div>
                    <div className="text-3xl font-bold text-green-400">${results.annualSavings?.toLocaleString()}</div>
                  </div>
                </div>
                <p className="text-xs text-slate-500 italic mt-4">*Assumes AI handles ~65% of routine Tier-1 call volume. Actual savings vary.</p>
              </div>
            )}

            {/* Tab 2: Time Saved */}
            {activeTab === 1 && (
              <div className="bg-slate-900 rounded-2xl p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold">Time Saved</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700">
                    <div className="text-xs text-slate-400 mb-1">Team Hours on Routine Calls/mo</div>
                    <div className="text-2xl font-bold">{results.totalStaffHours} hrs</div>
                    <div className="text-xs text-slate-500 mt-1">currently spent on Tier-1 calls</div>
                  </div>
                  <div className="bg-green-900/40 p-4 rounded-xl border border-green-500/30">
                    <div className="text-xs text-green-300 mb-1">Hours AI Takes Off Their Plate</div>
                    <div className="text-2xl font-bold text-green-400">{results.aiHoursReplaced} hrs/mo</div>
                    <div className="text-xs text-green-300/70 mt-1">returned to your team</div>
                  </div>
                </div>
                <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mb-4">
                  <div className="text-center mb-4">
                    <div className="text-xs text-slate-400 mb-1">That's equivalent to</div>
                    <div className="text-5xl font-bold text-white">{results.workDaysSaved}</div>
                    <div className="text-lg text-slate-300 font-medium">full work days per month</div>
                    <div className="text-sm text-green-400 mt-1">{results.annualWorkDaysSaved} work days per year your team gets back</div>
                  </div>
                </div>
                <div className="bg-slate-800/60 rounded-xl p-5 border border-slate-700">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Your team refocuses on:</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {['Complex troubleshooting', 'Escalations & edge cases', 'Proactive customer outreach', 'Projects that move the business forward'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0"></div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Tab 3: Customer Experience */}
            {activeTab === 2 && (
              <div className="bg-slate-900 rounded-2xl p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <SafeIcon icon={FiStar} className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold">Customer Experience Impact</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-5">
                  <div className="bg-red-900/30 p-4 rounded-xl border border-red-500/30">
                    <div className="text-xs text-red-300 mb-1">Calls Going to Voicemail Now</div>
                    <div className="text-2xl font-bold text-red-400">{results.missedCalls}/mo</div>
                    <div className="text-xs text-red-300/70 mt-1">customers who don't get an answer</div>
                  </div>
                  <div className="bg-green-900/40 p-4 rounded-xl border border-green-500/30">
                    <div className="text-xs text-green-300 mb-1">With Valtivo</div>
                    <div className="text-2xl font-bold text-green-400">0 missed calls</div>
                    <div className="text-xs text-green-300/70 mt-1">every caller answered in &lt;2 seconds</div>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Average hold time eliminated', value: 'Instant answer — no queue' },
                    { label: 'After-hours coverage added', value: 'Nights, weekends & holidays' },
                    { label: '#1 driver of support satisfaction', value: 'Reduced wait times — now solved' },
                    { label: '#2 driver of support satisfaction', value: '24/7 availability — now solved' },
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between items-center py-3 border-b border-slate-700/50">
                      <span className="text-slate-400 text-sm">{item.label}</span>
                      <span className="text-white text-sm font-semibold text-right max-w-[50%]">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bottom line summary */}
            <div className="bg-green-50 border border-green-100 rounded-2xl p-5">
              <p className="text-green-900 font-semibold text-sm md:text-base text-center leading-relaxed">
                Valtivo saves you <span className="font-bold text-green-700">${results.netSavings?.toLocaleString()}/month</span>, gives your team back{' '}
                <span className="font-bold text-green-700">{results.workDaysSaved} work days/month</span>, and ensures every customer call is answered instantly — 24/7.
              </p>
            </div>

            <button
              onClick={() => document.getElementById('booking-section').scrollIntoView({ behavior: 'smooth' })}
              className="w-full bg-green-700 text-white py-4 rounded-xl font-bold hover:bg-green-800 transition-colors shadow-lg flex items-center justify-center gap-2"
            >
              Get Your Custom Savings Analysis
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;